import { useEffect, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import {
  ContentCardGrid,
  FormCards,
  ListRows,
  RecordSheet,
  TableRows,
  TextLines,
  TileGrid,
  WeekCalendar,
} from './loading-silhouettes.js';

export type LoadingStateType =
  'card' | 'list' | 'table' | 'grid' | 'text' | 'tile' | 'fiche' | 'calendar';

export interface LoadingStateProps extends ComponentProps<'div'> {
  type?: LoadingStateType;
  count?: number;
}

/**
 * Attente avant d'afficher le squelette. En dessous, le contenu arrive sans
 * clignotement ; au-dessus, la silhouette de l'écran prend la place.
 */
export const SKELETON_DELAY_MS = 200;

function useRevealAfterDelay(delayMs: number): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setVisible(true);
    }, delayMs);
    return () => {
      window.clearTimeout(handle);
    };
  }, [delayMs]);

  return visible;
}

function defaultCount(type: LoadingStateType): number {
  switch (type) {
    case 'card':
    case 'fiche':
    case 'calendar':
      return 1;
    case 'list':
    case 'table':
    case 'grid':
    case 'tile':
    case 'text':
      return 3;
    default: {
      const exhaustive: never = type;
      return exhaustive;
    }
  }
}

function silhouette(type: LoadingStateType, count: number): ReactNode {
  switch (type) {
    case 'card':
      return <FormCards count={count} />;
    case 'tile':
      return <TileGrid count={count} />;
    case 'list':
      return <ListRows count={count} />;
    case 'table':
      return <TableRows count={count} />;
    case 'grid':
      return <ContentCardGrid count={count} />;
    case 'fiche':
      return <RecordSheet count={count} />;
    case 'calendar':
      return <WeekCalendar />;
    case 'text':
      return <TextLines count={count} />;
    default: {
      const exhaustive: never = type;
      return exhaustive;
    }
  }
}

/**
 * Un seul squelette, une variante par forme de contenu : formulaire, tuile,
 * liste, tableau, grille de cartes, fiche, semaine. Jamais deux dalles anonymes.
 */
export function LoadingState({ type = 'card', count, className, ...props }: LoadingStateProps) {
  const visible = useRevealAfterDelay(SKELETON_DELAY_MS);
  if (!visible) return null;

  return (
    <div
      className={className}
      role="status"
      aria-busy="true"
      data-testid={`loading-state-${type}`}
      {...props}
    >
      {silhouette(type, count ?? defaultCount(type))}
    </div>
  );
}
